import { Injectable } from '@angular/core';
import { RemoteData, appEntities } from '@lycoris-nubila/ngx-lycoris';
import { Observable, from, of } from 'rxjs';
import { catchError, map, startWith, switchMap, tap } from 'rxjs/operators';

import { PhaseOutput } from 'phase/model/phase.output';
import { IPhase } from 'phase/phase';
import { PhaseConverterPort } from 'phase/port/phase-converter.port';
import { Section } from 'phase/section/section';
import { UserRepositoryPort } from 'user/port/user-repository.port';

import { CacheObservable } from '../../app-decorators';
import { PhaseApiPort } from './port/phase-api.port';
import { PhaseApplicationPort } from './port/phase-application.port';
import { PhaseRepositoryPort } from './port/phase-repository.port';
import { SectionConverterPort } from './section/port/section-converter.port';
import { SectionRepositoryPort } from './section/port/section-repository.port';

@Injectable()
export class PhaseApplicationAdapter implements PhaseApplicationPort {
	constructor(
		private _phaseApiPort: PhaseApiPort,
		private _userRepositoryPort: UserRepositoryPort,
		private _phaseRepositoryPort: PhaseRepositoryPort,
		private _sectionRepositoryPort: SectionRepositoryPort,
		private _phaseConverterPort: PhaseConverterPort,
		private _sectionConverterPort: SectionConverterPort
	) {}

	@CacheObservable()
	public observePhasesByUserId(
		userId: string
	): Observable<RemoteData<IPhase[]>> {
		const api$ = from(this._phaseApiPort.fetchPhasesByUserId(userId)).pipe(
			tap((phases) =>
				this._sectionRepositoryPort.saveSections(this._extractSections(phases))
			),
			tap((phases) =>
				this._phaseRepositoryPort.savePhases(
					this._phaseConverterPort.phasesAsEntities(phases)
				)
			),
			map(
				(phases) => ({ data: phases, fresh: true } as RemoteData<PhaseOutput[]>)
			),
			catchError((_) =>
				of({ fresh: true, data: [] } as RemoteData<PhaseOutput[]>)
			),
			startWith({ fresh: false, data: [] } as RemoteData<PhaseOutput[]>)
		);

		const repo$ = this._userRepositoryPort
			.observeUserById(userId)
			.pipe(
				switchMap((user) =>
					user.organizationId
						? this._phaseRepositoryPort.observePhasesByOrganizationId(
								user.organizationId
						  )
						: of([])
				)
			);

		return appEntities(api$, repo$);
	}

	private _extractSections(phases: PhaseOutput[]): Section[] {
		return phases
			.map((phase) =>
				this._sectionConverterPort.sectionsAsEntities(phase.sections, phase.id)
			)
			.reduce((p, c) => [...p, ...c], []);
	}
}
